import React, { FC, useCallback, useEffect, useState } from 'react'
import { useRef } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { get } from 'src/apis/apiService'
import { LIVE_SESSION_APIS } from 'src/apis/liveSessionApi'
import { MessageType } from 'src/components/LiveQuestions/LiveQuestions'
import LoadIcon from 'src/components/LoadIcon'
import { getHallQuestionDb } from 'src/firebaseConfig'
import { AdminQuestionCard } from '../../components/LiveQuestions/AdminQuestionCard'
import useQuestion from '../../utils/questionHelper'
import { PageStyle } from './styled'

export enum QuestionTabEnum {
  'All' = 'All',
  'Unpresented' = 'Unpresented',
  'Presented' = 'Presented',
  'Archived' = 'Archived',
}

export const AdminLiveQuestionPage: FC<RouteComponentProps> = ({ match }) => {
  const sessionId = match.params['sessionId']

  const [sessions, setSessions] = useState([])
  const [pageLoad, setPageLoad] = useState(true)
  const { messages, messagesLoading, setQuestionStatus } = useQuestion(getHallQuestionDb(sessionId))
  const [pinned, setPinned] = useState<MessageType & { key: string }>(null)
  const [currentTab, setCurrentTab] = useState<QuestionTabEnum>(QuestionTabEnum.All)
  const questionsRef = useRef<HTMLSelectElement>(null)

  const fetchSessions = useCallback(() => {
    get(
      LIVE_SESSION_APIS.GET_IDS,
      (data) => {
        setSessions(data.all_sessions)
        setPageLoad(false)
      },
      (error) => {
        console.error(error)
        setPageLoad(false)
      }
    )
  }, [])

  useEffect(fetchSessions, [])

  useEffect(() => {
    const pinnedQuestion = messages.filter((ques) => ques.featured)
    if (pinnedQuestion.length > 0) {
      setPinned(pinnedQuestion[0])
    } else {
      setPinned(null)
    }
  }, [messages])

  useEffect(() => {
    if (questionsRef && questionsRef.current) {
      questionsRef.current.scrollTop = questionsRef.current.scrollHeight
    }
  }, [messagesLoading, messages.length])

  const getQuestionsByTab = (): Array<MessageType> => {
    switch (currentTab) {
      case QuestionTabEnum.Archived:
        return messages.filter((question) => question.archived) as Array<MessageType>

      case QuestionTabEnum.Presented:
        return messages.filter((question) => question.show) as Array<MessageType>

      case QuestionTabEnum.Unpresented:
        return messages.filter((question) => !question.archived && !question.show) as Array<MessageType>

      case QuestionTabEnum.All:
        return messages.filter((question) => !question.archived) as Array<MessageType>

      default:
        return messages
    }
  }

  const onTabSwitch = (e) => {
    setCurrentTab(e.target.value)
  }

  const handleArchieved = (mes) => {
    setQuestionStatus(mes.key, 'archived', !mes.archived)
  }

  const handleFeatured = (mes) => {
    if (pinned) {
      setQuestionStatus(pinned.key, 'featured', !pinned.featured)
    }
    setQuestionStatus(mes.key, 'featured', !mes.featured)
  }

  const handleShow = (mes) => {
    setQuestionStatus(mes.key, 'show', !mes.show)
  }

  if (pageLoad) {
    return <LoadIcon />
  }

  return (
    <PageStyle>
      <div className="pageContainer">
        <div className="badgeSelection">
          {sessions.map(({ id, title }) => (
            <a href={`/live-questions/${id}`} key={id}>
              <span className={`text-truncate badge rounded-pill ${sessionId == id ? 'badge-danger' : 'badge-secondary'}`}>{id} : {title}</span>
            </a>
          ))}
        </div>

        <header className="d-flex justify-content-between align-items-center flex-wrap">
          <h5>Auditorium Live Question ({sessions.find(s => s.id == sessionId)?.title})</h5>
          <div>
            <select className="form-input" value={currentTab} name="tab" onChange={onTabSwitch}>
              {Object.keys(QuestionTabEnum).map((item, i) => (
                <option key={`tab-${i}`} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
        </header>
        <section className="questionsContainer" ref={questionsRef}>
          {getQuestionsByTab().map((message, index) => (
            <AdminQuestionCard
              key={message.dbTimestamp}
              message={message}
              handleArchieved={handleArchieved}
              handleFeatured={handleFeatured}
              handleShow={handleShow}
            />
          ))}
        </section>
      </div>
    </PageStyle>
  )
}